import {
  Select as NextUISelect,
  SelectItem as NextUISelectItem,
  PopoverProps,
} from "@heroui/react";
import clsx from "clsx";
import { WithId } from "core/types";
import { ReactNode, useMemo } from "react";
import { tv } from "tailwind-variants";

const selectVariants = tv({
  compoundVariants: [
    // [inline, outline] & color
    {
      class: {
        trigger: [
          "data-[open=true]:ring-primary",
          "data-[focus=true]:ring-primary",
        ],
      },
      color: "primary",
      variant: ["inline", "outline"],
    },
    // labelPlacement=outside & default
    {
      class: {
        label: "group-data-[filled=true]:text-gray-900",
      },
      color: "default",
      labelPlacement: "outside",
    },
    // isDisabled & variant
    {
      class: {
        label: "!text-default-disabled",
        trigger: ["bg-white !text-default-disabled"],
        value: "!text-default-disabled",
      },
      isDisabled: true,
      variant: ["minimal", "horizontal"],
    },
    // isInvalid & variant
    {
      class: {
        trigger: "!ring-danger group-data-[focus=true]:ring-danger",
      },
      isInvalid: true,
      variant: ["inline", "outline"],
    },
    {
      class: {
        trigger: "bg-white",
      },
      isInvalid: true,
      variant: ["minimal", "horizontal"],
    },
    // isInvalid & variant & size
    {
      class: {
        errorMessage: "pl-2",
      },
      isInvalid: true,
      size: "md",
      variant: ["minimal", "horizontal"],
    },
    {
      class: {
        errorMessage: "pl-2.5",
      },
      isInvalid: true,
      size: "lg",
      variant: ["minimal", "horizontal"],
    },
    // size & labelPlacement
    {
      class: {
        trigger: "h-8 min-h-8 py-1.5 px-3",
      },
      labelPlacement: "inside",
      size: "sm",
    },
    {
      class: {
        trigger: "h-10 min-h-10 py-2",
      },
      labelPlacement: "inside",
      size: "md",
    },
    {
      class: {
        label: "text-sm",
        trigger: "h-12 min-h-12 py-2.5 gap-0",
      },
      labelPlacement: "inside",
      size: "lg",
    },
    {
      class: {
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
    },
    {
      class: {
        base: "group relative justify-end",
        label: [
          "pb-0",
          "z-20",
          "top-1/2",
          "-translate-y-1/2",
          "group-data-[filled=true]:start-0",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
    },
    // labelPlacement=[inside]
    {
      class: {
        label:
          "group-data-[filled=true]:scale-100 group-data-[filled=true]:text-[10px]",
      },
      labelPlacement: ["inside"],
    },
    // inside & size
    {
      class: {
        label: "text-sm",
      },
      labelPlacement: "inside",
      size: ["sm", "md"],
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-4",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_theme(fontSize.sm)/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-4",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-5",
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+__10px/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
    },
    // inside & size & inline
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_8px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
      variant: "inline",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
      variant: "inline",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-3",
        label: [
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(50%_+_10px/2_-_6px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
      variant: "inline",
    },
    // inside & size & minimal
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "sm",
      variant: "minimal",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "md",
      variant: "minimal",
    },
    {
      class: {
        innerWrapper: "group-data-[has-label=true]:pt-2",
        label: ["!text-sm !translate-y-0"],
      },
      isMultiline: false,
      labelPlacement: "inside",
      size: "lg",
      variant: "minimal",
    },
    // outside & size
    {
      class: {
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_8px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_16px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "sm",
    },
    {
      class: {
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_10px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_20px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "md",
    },
    {
      class: {
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_12px)]",
        label: [
          "start-0",
          "text-sm",
          "font-medium text-gray-900",
          "!-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_24px)]",
        ],
      },
      isMultiline: false,
      labelPlacement: "outside",
      size: "lg",
    },
    // outside-left & size
    {
      class: {
        label: ["h-8 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "sm",
    },
    {
      class: {
        label: ["h-10 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "md",
    },
    {
      class: {
        label: ["h-12 flex items-center"],
      },
      isMultiline: false,
      labelPlacement: "outside-left",
      size: "lg",
    },
    // isMultiline & labelPlacement="outside"
    {
      class: {
        label: "pb-1.5",
      },
      isMultiline: true,
      labelPlacement: "outside",
    },
    // text truncate labelPlacement=[inside,outside]
    {
      class: {
        label: ["pe-2", "max-w-full", "text-ellipsis", "overflow-hidden"],
      },
      labelPlacement: ["inside", "outside"],
    },
  ],
  defaultVariants: {
    color: "primary",
    disableSelectorIconRotation: false,
    fullWidth: true,
    isDisabled: false,
    isMultiline: false,
    labelPlacement: "inside",
    size: "md",
    variant: "inline",
  },
  slots: {
    base: "group inline-flex flex-col relative",
    description: "",
    errorMessage: "",
    helperWrapper: "",
    innerWrapper: "",
    label:
      "block absolute z-10 subpixel-antialiased pointer-events-none text-foreground-500",
    listbox: "",
    listboxWrapper: "",
    mainWrapper: "",
    popoverContent: "",
    selectorIcon: "text-primary",
    trigger:
      "relative px-3 w-full inline-flex outline-none tap-highlight-transparent group-data-[focus=true]:bg-default-200",
    value: "text-foreground-500 w-full text-start",
  },
  variants: {
    color: {
      danger: {},
      default: {},
      primary: {},
      secondary: {},
      success: {},
      warning: {},
    },
    disableAnimation: {
      false: {
        base: "transition-background motion-reduce:transition-none !duration-150",
        label: [
          "will-change-auto",
          "origin-top-left",
          // Using RTL here as Tailwind CSS doesn't support `start` and `end` logical properties for transforms yet.
          "rtl:origin-top-right",
          "!duration-200",
          "!ease-out",
          "transition-[transform,color,left,opacity]",
          "motion-reduce:transition-none",
        ],
        selectorIcon:
          "transition-transform duration-150 ease motion-reduce:transition-none",
      },
      true: {
        base: "transition-none",
        label: "transition-none",
        selectorIcon: "transition-none",
        trigger: "after:transition-none",
      },
    },
    disableSelectorIconRotation: {
      false: {
        selectorIcon: "data-[open=true]:rotate-180",
      },
      true: {},
    },
    fullWidth: {
      false: {
        base: "min-w-40",
      },
      true: {
        base: "w-full",
      },
    },
    isDisabled: {
      true: {
        base: "opacity-100 pointer-events-none",
        trigger: "pointer-events-none bg-gray-200",
      },
    },
    isInvalid: {
      true: {
        errorMessage: "text-danger-dark",
        label: "text-danger",
        selectorIcon: "text-danger",
        trigger: "border-danger",
        value: "text-danger",
      },
    },
    isMultiline: {
      false: {
        value: "truncate",
      },
      true: {
        label: "relative min-h-fit",
        trigger: "!h-auto",
      },
    },
    isRequired: {
      true: {
        label: "after:content-['*'] after:text-danger after:ms-0.5",
      },
    },
    labelPlacement: {
      inside: {
        label: "text-sm cursor-pointer",
        trigger: "flex-col items-start justify-center gap-0",
      },
      outside: {
        base: "flex flex-col",
        label: "text-sm",
        value: "font-normal text-sm",
      },
      "outside-left": {
        base: "flex-row items-center flex-nowrap items-start",
        label: "relative pe-2 text-foreground flex items-center",
      },
    },
    radius: {
      full: {
        trigger: "rounded-full",
      },
      lg: {
        trigger: "rounded-large",
      },
      md: {
        trigger: "rounded-medium",
      },
      none: {
        trigger: "rounded-none",
      },
      sm: {
        trigger: "rounded-small",
      },
    },
    size: {
      lg: {
        trigger: "h-12 min-h-12 rounded-large",
        value: "text-medium",
      },
      md: {
        trigger: "h-10 min-h-10 rounded-medium",
        value: "text-small",
      },
      sm: {
        label: "text-tiny",
        trigger: "h-8 min-h-8 px-2 rounded-small",
        value: "text-small",
      },
    },
    variant: {
      horizontal: {
        innerWrapper: ["!pt-0"],
        label:
          "min-w-max font-medium group-data-[hover=true]:text-gray-900 !text-sm",
        listbox: "[&>*]:gap-1 min-w-full w-max overflow-y-auto max-h-60",
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
          "w-auto min-w-[80px]",
        ],
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal group-data-[hover=true]:text-gray-900",
        ],
      },
      inline: {
        label:
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-normal group-data-[has-value=true]:font-medium",
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
      },
      minimal: {
        innerWrapper: ["!pt-0"],
        label:
          "group-data-[has-value=true]:!hidden group-data-[hover=true]:text-gray-900",
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-transparent",
          "data-[hover=true]:bg-white",
          "data-[open=true]:bg-white",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: [
          "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
        ],
      },
      outline: {
        label: [
          "text-gray-600 group-data-[hover=true]:text-gray-900 text-sm font-medium",
        ],
        listbox: "[&>*]:gap-1 overflow-y-auto max-h-60", // necessary due to NextUI bug - both the div and the ul has the data-slot="listbox"
        trigger: [
          "group shadow-none bg-white",
          "ring-1 ring-inset",
          "ring-gray-600",
          "data-[hover=true]:ring-black data-[hover=true]:ring-2 data-[hover=true]:bg-white",
          "data-[open=true]:ring-primary data-[open=true]:ring-2",
          "data-[focus=true]:ring-primary data-[focus=true]:ring-2",
        ],
        value: "group-data-[has-value=true]:text-gray-900 !text-sm font-normal",
      },
    },
  },
});

type NextUISelectProps = React.ComponentProps<typeof NextUISelect>;

type Variant = "inline" | "outline" | "minimal" | "horizontal";

export type SelectProps<T extends WithId = WithId> = Omit<
  NextUISelectProps,
  "variant" | "color" | "radius" | "labelPlacement" | "children" | "classNames"
> & {
  items: T[];
  renderItem: (item: T) => ReactNode;
  variant?: Variant;
};

function useSelectedKeysConvert<T>(selectedKeys: T): Set<string> {
  const stableKey = useMemo(() => {
    if (selectedKeys == null) return "";
    if (selectedKeys instanceof Set) {
      return Array.from(selectedKeys).sort().join(",");
    }
    if (Array.isArray(selectedKeys)) {
      return selectedKeys.map(String).sort().join(",");
    }
    return String(selectedKeys);
  }, [selectedKeys]);

  return useMemo(() => {
    if (selectedKeys == null) return new Set<string>();
    if (selectedKeys instanceof Set) return selectedKeys as Set<string>;
    if (Array.isArray(selectedKeys)) return new Set(selectedKeys.map(String));
    return new Set([String(selectedKeys)]);
  }, [stableKey]);
}

function getLabelPlacement(variant?: Variant) {
  if (variant === "inline" || variant === "minimal") return "inside";
  if (variant === "outline") return "outside";
  if (variant === "horizontal") return "outside-left";
  return undefined;
}

export function Select<T extends WithId = WithId>({
  renderItem,
  selectedKeys,
  variant,
  ...props
}: SelectProps<T>) {
  const convertToSet = useSelectedKeysConvert(selectedKeys);
  const computedLabelPlacement = useMemo(
    () => getLabelPlacement(variant) ?? "inside",
    [variant],
  );

  const classNames = useMemo(() => {
    const slots = selectVariants({
      color: "primary",
      disableAnimation: props.disableAnimation ?? false,
      disableSelectorIconRotation: props.disableSelectorIconRotation ?? false,
      fullWidth: props.fullWidth ?? true,
      isDisabled: props.isDisabled ?? false,
      isInvalid: props.isInvalid ?? false,
      isMultiline: props.isMultiline ?? false,
      isRequired: props.isRequired ?? false,
      labelPlacement: computedLabelPlacement,
      size: props.size ?? "md",
      variant: variant ?? "inline",
    });

    return {
      base: slots.base(),
      description: slots.description(),
      errorMessage: slots.errorMessage(),
      helperWrapper: slots.helperWrapper(),
      innerWrapper: slots.innerWrapper(),
      label: slots.label(),
      listbox: slots.listbox(),
      listboxWrapper: slots.listboxWrapper(),
      mainWrapper: slots.mainWrapper(),
      popoverContent: slots.popoverContent(),
      selectorIcon: slots.selectorIcon(),
      trigger: slots.trigger(),
      value: slots.value(),
    };
  }, [
    props.disableAnimation,
    props.disableSelectorIconRotation,
    props.fullWidth,
    props.isDisabled,
    props.isInvalid,
    props.isMultiline,
    props.isRequired,
    props.size,
    variant,
    computedLabelPlacement,
  ]);

  return (
    <NextUISelect
      {...props}
      classNames={classNames}
      color="primary"
      data-testid={props.name}
      labelPlacement={computedLabelPlacement}
      popoverProps={
        { "data-testid": `${props.name}-popover` } as unknown as PopoverProps
      }
      selectedKeys={convertToSet}
      title={typeof props.title === "string" ? props.title : undefined}
    >
      {(item: object) => {
        const typedItem = item as T;
        return (
          <NextUISelectItem
            key={typedItem.id}
            classNames={{
              base: clsx(
                "!text-gray-900",
                "rounded-lg !bg-white p-2 !outline-none ring-1 ring-transparent",
                "data-[focus=true]:!bg-primary-light data-[focus=true]:ring-primary",
                "data-[hover=true]:!bg-primary-light data-[hover=true]:ring-primary",
                "data-[selected=true]:!bg-primary-light data-[selected=true]:font-semibold data-[selected=true]:ring-primary",
              ),
            }}
          >
            {renderItem(typedItem)}
          </NextUISelectItem>
        );
      }}
    </NextUISelect>
  );
}

Select.displayName = "RecareUI.Select";
